<template>
  <LayoutMain>
    <template slot="aside" v-if="asyncDataStatus_isReady">
      <div class="steps_list">
        <div
          v-for="(step, i) in requiredSteps"
          :key="step.id"
          class="steps_list__item"
          :class="stepItemClassNames(i)"
          @click="handleStepItemClick($event, step.id)"
        >
          <div class="steps_list__item_title">
            <span class="steps_list__item_title_text_big_screen">{{
              $t(step.title)
            }}</span>
            <span class="steps_list__item_title_text_small_screen">{{
              $t(step.titleShort) ? $t(step.titleShort) : $t(step.title)
            }}</span>
          </div>
          <div class="steps_list__item_circle">
            <span class="steps_list__item_num">{{ i + 1 }}</span>
            <img
              class="steps_list__item_check"
              src="@/assets/img/check--success.svg"
            />

            <div class="steps_list__item_incomplete_icon__wrapper">
              <b-icon
                class="steps_list__item_incomplete_icon"
                icon="alert-circle"
                size="is-small"
              ></b-icon>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template slot="body">
      <div class="calculator_body">
        <template v-if="asyncDataStatus_isReady">
          <transition :name="slideTransitionName" mode="out-in">
            <keep-alive :include="cachedKeepAliveComponents">
              <component
                :is="activeStep.component"
                class="calc_step"
                @clear-keep-alive-cache="handleClearKeepAliveCache"
                @start-calculation="handleStartCalculationFromStep"
                @prev="navToPrevStep"
                @next="navToNextStep"
              ></component>
            </keep-alive>
          </transition>

          <app-loading
            :active.sync="isCalculatingOverlayOpen"
            :title="$t('O seu cálculo está a ser efectuado.')"
          >
            <p>{{ $t("Poderá demorar alguns segundos.") }}</p>
          </app-loading>

          <div
            class="json is-fixed_left"
            v-if="isDev"
            :class="{ 'is-open': isJsonOpen }"
          >
            <div class="json__toggle" @click="() => (isJsonOpen = !isJsonOpen)">
              <div class="json__toggle_icon"></div>
            </div>

            <div class="json__body">
              <pre><code v-html="JSON.stringify(this.calculation.input, null, 2)"></code></pre>
            </div>
          </div>
        </template>
      </div>

      <div class="desktop_only_overlay">
        <div class="desktop_only_overlay__content">
          <b-icon
            class="desktop_only_overlay__icon"
            icon="laptop"
            size="is-large"
          />
          <p class="desktop_only_overlay__text">
            {{
              $t(
                "O simulador funciona apenas em dispositivos de ecrã mais largo."
              )
            }}
          </p>
        </div>
      </div>
    </template>
  </LayoutMain>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { asyncDataStatus } from "@/mixins";

import LayoutMain from "@/layouts/l-main.vue";

import {
  ModalNameCalc,
  ModalAuth,
  CalcStepGeneral,
  CalcStepLoads,
  CalcStepWind,
  CalcStepSnow,
  CalcStepAdditional,
  CalcStepResults,
} from "@/components";

export default {
  mixins: [asyncDataStatus],

  components: {
    LayoutMain,
    ModalNameCalc,
    ModalAuth,
  },

  data() {
    return {
      isDev: false,
      pressedKeys: [],
      pressedKeysTimeout: null,
      handleDevKeyPressBinded: undefined,
      isModalNameCalcOpen: false,
      isModalAuthOpen: false,
      isCalculatingOverlayOpen: false,

      isStepIncompleteHighlightActive: false,

      lastActiveStepIdx: 0,
      activeStep: undefined,

      cachedKeepAliveComponents: undefined,

      steps: [
        {
          id: "general",
          title: "Parâmetros gerais",
          titleShort: "P. gerais",
          component: CalcStepGeneral,
        },
        {
          id: "loads",
          title: "Cargas",
          component: CalcStepLoads,

          isComplete() {
            if (!this.calculation) return true;
            return this.isMinMaxValid("permanent", "distributed");
          },
        },
        {
          id: "wind",
          title: "Vento",
          component: CalcStepWind,

          isComplete() {
            if (!this.calculation) return true;

            return (
              this.isWindPressureValid &&
              this.isWindSuctionValid &&
              this.isWindLocalPressureValid &&
              this.isWindLocalSuctionValid
            );
          },
        },
        {
          id: "snow",
          title: "Neve",
          component: CalcStepSnow,

          isComplete() {
            if (!this.calculation) return true;
            return this.isSnowAccumulationValid;
          },

          isRequired() {
            if (!this.calculation) return true;
            return this.calculation.input.type !== 2;
          },
        },
        {
          id: "additional",
          title: "Parâmetros adicionais",
          titleShort: "P. adicionais",
          component: CalcStepAdditional,

          isComplete() {
            return true;
          },
        },
        {
          id: "results",
          title: "Resultados",
          component: CalcStepResults,
        },
      ].map((step) => ({
        //defaults
        isComplete: () => true,
        isRequired: () => true,
        ...step,
      })),

      isJsonOpen: false,
    };
  },

  computed: {
    ...mapState([
      "user",
      "calculationData",
      "usersCalculations",
      "selectedLang",
    ]),

    ...mapState("calculator", ["calculation", "rawCalculationId"]),

    ...mapGetters("calculator", [
      "hasUserInput",
      "isWindPressureValid",
      "isWindSuctionValid",
      "isWindLocalPressureValid",
      "isWindLocalSuctionValid",
      "isSnowAccumulationValid",
      "isMinMaxValid",
    ]),

    rawCalculation() {
      return this.usersCalculations.find(
        ({ id }) => id === this.rawCalculationId
      );
    },

    requiredSteps() {
      return this.steps.filter((step) => step.isRequired.call(this));
    },

    requiredStepsIds() {
      return this.requiredSteps.map(({ id }) => id);
    },

    activeStepIdx() {
      if (this.activeStep) {
        return this.requiredSteps.findIndex(
          ({ id }) => id === this.activeStep.id
        );
      } else {
        return null;
      }
    },

    stepItemClassNames() {
      return function (stepIdx) {
        const isComplete = this.requiredSteps[stepIdx].isComplete.call(this);

        return [
          stepIdx === this.activeStepIdx ? "is-active" : "",
          stepIdx < this.activeStepIdx ? "is-beforeActive" : "",
          isComplete ? "is-complete" : "is-incomplete",
          !isComplete && this.isStepIncompleteHighlightActive
            ? "is-incompleteHighlight"
            : "",
          stepIdx === this.requiredSteps.length - 1 ? "is-results" : "",
        ];
      };
    },

    incompleteSteps() {
      return this.requiredSteps.filter((step) => {
        return !step.isComplete.call(this);
      });
    },

    slideTransitionName() {
      return this.activeStepIdx > this.lastActiveStepIdx
        ? "slideAnim-next"
        : "slideAnim-prev";
    },
  },

  watch: {
    activeStepIdx(val, oldVal) {
      this.lastActiveStepIdx = oldVal;
    },

    requiredSteps() {
      if (this.activeStep) {
        if (!this.requiredStepsIds.includes(this.activeStep.id)) {
          this.navToStep("general");
        }
      }
    },
  },

  methods: {
    ...mapActions([
      "fetchUsersCalculation",
      "fetchCountries",
      "createUsersCalculation",
    ]),

    ...mapActions("calculator", {
      calculatorBoot: "boot",
      calculatorSubmit: "submit",
      calculatorDestroy: "destroy",
    }),

    handleDevKeyPress(e) {
      const key = e.key;
      this.pressedKeys.push(key);

      const keySequence = this.pressedKeys.join("").toLowerCase();

      if (keySequence === "dev") {
        this.isDev = !this.isDev;
      }

      clearTimeout(this.pressedKeysTimeout);
      this.pressedKeysTimeout = setTimeout(() => {
        this.pressedKeys = [];
      }, 300);
    },

    isValidNavToStep(stepId) {
      if (
        !this.requiredStepsIds.includes(stepId) ||
        (stepId === "results" && !this.rawCalculation)
      ) {
        return false;
      }

      return true;
    },

    navToStep(stepId) {
      if (
        (this.activeStep && stepId === this.activeStep.id) ||
        !this.isValidNavToStep(stepId)
      )
        return;

      this.activeStep = this.requiredSteps.find(({ id }) => id === stepId);

      const routerObj = {
        hash: stepId,
      };

      if (this.rawCalculation) {
        routerObj.query = {
          calculationId: this.rawCalculationId,
        };
      }

      this.$router.push(routerObj);
    },

    navToPrevStep() {
      if (this.activeStepIdx !== 0) {
        const prevStep = this.requiredSteps[this.activeStepIdx - 1];
        this.navToStep(prevStep.id);
      }
    },

    navToNextStep() {
      if (this.activeStepIdx !== this.requiredSteps.length - 1) {
        const nextStep = this.requiredSteps[this.activeStepIdx + 1];
        this.navToStep(nextStep.id);
      }
    },

    //event listeners
    handleClearKeepAliveCache() {
      //destroy all but first of steps
      const copy = this.cachedKeepAliveComponents;
      this.cachedKeepAliveComponents = [this.cachedKeepAliveComponents[0]];
      this.$nextTick(() => {
        this.cachedKeepAliveComponents = copy;
      });
    },

    handleStepItemClick(e, stepId) {
      if (stepId === "results" && this.activeStep.id !== "results") {
        this.$buefy.notification.open({
          duration: 4000,
          queue: false,
          message: this.$t(
            `Clique em "Continuar" no último passo para aceder aos resultados.`
          ),
          position: "is-bottom-right",
          type: "is-danger",
          animation: "fade",
          hasIcon: true,
        });
      } else {
        this.navToStep(stepId);
      }
    },

    handleStartCalculationFromStep() {
      if (!this.hasUserInput) {
        this.$buefy.notification.open({
          duration: 4000,
          queue: false,
          message: this.$t(
            "Não pode efectuar cálculo a menos que tenha alguma carga definida."
          ),
          position: "is-bottom-right",
          type: "is-danger",
          hasIcon: true,
        });
      } else if (this.incompleteSteps.length) {
        const incompleteStepsText = this.incompleteSteps.reduce(
          (result, { id, title }, i) => {
            const idx = this.getStepIdx(id);
            const stepText = `${idx + 1}) ${title}`;

            if (i === 0) {
              return stepText;
            } else if (i === this.incompleteSteps.length - 1) {
              return `${result} e ${stepText}`;
            } else {
              return `${result}, ${stepText}`;
            }
          },
          ""
        );

        this.$buefy.notification.open({
          duration: 4000,
          queue: false,
          message: this.$t(
            "Certifique-se que todos os campos no(s) passo(s) {text} estão corretos antes de continuar.",
            null,
            { text: incompleteStepsText }
          ),
          position: "is-bottom-right",
          type: "is-danger",
          hasIcon: true,
        });

        this.isStepIncompleteHighlightActive = true;
      } else {
        this.startCalculation();
      }
    },

    startCalculation() {
      this.isCalculatingOverlayOpen = true;

      this.calculatorSubmit()
        .then(() => {
          this.navToStep("results");
        })
        .catch((err) => {
          this.$buefy.notification.open({
            indefinite: true,
            queue: false,
            message: err.message,
            position: "is-bottom-right",
            type: "is-danger",
            hasIcon: true,
          });

          throw err;
        })
        .finally(() => {
          this.isCalculatingOverlayOpen = false;
        });
    },

    getStepIdx(stepId) {
      return this.requiredStepsIds.findIndex((_stepId) => _stepId === stepId);
    },
  },

  created() {
    this.handleDevKeyPressBinded = this.handleDevKeyPress.bind(this);
    window.addEventListener("keydown", this.handleDevKeyPressBinded);

    this.cachedKeepAliveComponents = this.steps.map(
      ({ component }) => component.name
    );

    Promise.all([
      this.fetchCountries(),
      this.$route.query.calculationId
        ? this.fetchUsersCalculation(this.$route.query.calculationId)
        : Promise.resolve(null),
    ])
      .then((res) => {
        return this.calculatorBoot(res[1]);
      })
      .then(() => {
        //display view
        const stepIdFromHash = this.$route.hash.slice(1);

        if (this.isValidNavToStep(stepIdFromHash) && this.rawCalculation) {
          this.navToStep(stepIdFromHash);
        } else {
          this.navToStep("general");
        }

        this.asyncDataStatus_done();
      })
      .catch((err) => {
        this.$buefy.notification.open({
          message: err.message,
          position: "is-bottom-right",
          type: "is-danger",
          hasIcon: true,
        });
      });
  },

  beforeRouteUpdate(to, from, next) {
    const stepIdFromHash = to.hash.slice(1);

    if (!this.isValidNavToStep(stepIdFromHash)) {
      return;
    }

    next();
  },

  destroyed() {
    window.removeEventListener("keydown", this.handleDevKeyPressBinded);
    this.calculatorDestroy();
  },
};
</script>

<style lang="scss" scoped>
.steps_list {
  position: relative;
  z-index: 1;
}

.steps_list__item {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: $font-family-title;
  text-transform: uppercase;
  cursor: pointer;
  font-size: 16px;
}
.steps_list__item:not(:last-child) {
  padding-bottom: 64px;
}
.steps_list__item:after {
  content: "";
  position: absolute;
  z-index: -1;
  right: 15px;
  width: 1px;
  background: #cccccc;
  top: 0;
  bottom: 0;
}

.steps_list__item_title {
  font-weight: 700;
  color: $black-5;
  transition: 0.25s;
}

.steps_list__item_circle {
  position: relative;
  $size: 32px;
  width: $size;
  height: $size;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cccccc;
  border-radius: 50px;
  background-color: $black-2;
  transition: 0.25s;
}

.steps_list__item_num {
  color: #979797;
  font-size: 12px;
  font-weight: 700;
  transition: 0.25s;
}

.steps_list__item_check {
  display: none;

  width: 15px;
  position: relative;
  transition: 0.25s;
}

.steps_list__item.is-beforeActive,
.steps_list__item.is-active,
.steps_list__item:hover {
  .steps_list__item_title,
  .steps_list__item_num {
    color: #fff;
  }

  .steps_list__item_circle {
    border-color: #fff;
  }
}

.steps_list__item.is-beforeActive,
.steps_list__item.is-active {
  .steps_list__item_circle {
    border-width: 2px;
  }
}

.steps_list__item.is-beforeActive.is-complete {
  &:after {
    content: "";
    position: absolute;
    z-index: -1;
    right: 15px;
    width: 2px;
    background: #fff;
    top: 0;
    bottom: 0;
    transform: translateX(50%);
  }

  // .steps_list__item_circle {
  // 	border-color: #a2cc3b;
  // }

  .steps_list__item_num {
    display: none;
  }

  .steps_list__item_check {
    display: block;
  }
}

.steps_list__item_incomplete_icon__wrapper {
  position: absolute;
  bottom: 100%;
  left: 100%;
  transform: translate(-50%, 50%);
}

.steps_list__item_incomplete_icon {
  display: none;

  color: $brand-l3;
  font-size: 18px;
  animation: incompleteIconAnimEnter 0.25s cubic-bezier(0.11, 0.24, 0.52, 2.51)
    both;
}

.steps_list__item.is-incomplete.is-incompleteHighlight {
  .steps_list__item_incomplete_icon {
    display: block;
  }
}

@keyframes incompleteIconAnimEnter {
  0% {
    transform: scale(0.1);
    opacity: 0.8;
  }
}

.steps_list__item.is-results {
  cursor: not-allowed;
}

::v-deep .steps_body {
  margin-bottom: 16px;
}

::v-deep .steps_nav {
  display: flex;
  justify-content: space-between;
}

::v-deep .steps_nav__btn:not(:last-child) {
  margin-right: 16px;
}

::v-deep .steps_nav__btn:first-child:last-child {
  margin-left: auto;
}

//responsive
.steps_list__item_title_text_small_screen {
  display: none;
}

.json {
  position: fixed;
  bottom: 0;
  top: 0;
  left: 0;
  transform: translateX(-100%);
  transition: 0.2s;
  background-color: #fff;
  z-index: 99;
  width: 26em;

  &.is-fixed_right {
    left: auto;
    right: 0;
    transform: translateX(100%);
  }
}
.json__toggle {
  position: absolute;
  left: 100%;
  bottom: 92px;
  border-radius: 0 8px 8px 0;
  font-size: 2em;
  background-color: #111;
  color: white;
  text-align: center;
  padding: 0.3em;
  cursor: pointer;
  line-height: 1;

  .json.is-fixed_right & {
    left: auto;
    right: 100%;
    border-radius: 8px 0 0 8px;
  }
}

.json__toggle_icon {
  position: relative;
  top: -3px;

  &:after {
    display: inline-block;
    content: "+";
  }
}

.json__body {
  overflow: auto;
  max-height: 100vh;
  padding: 2em;
}

.json__body pre,
.json__body code {
  background-color: transparent;
}

//state
.json.is-open {
  transform: translateX(0%);
  box-shadow: -4px 0 37px #00000042;

  .json__toggle_icon {
    &:after {
      content: "-";
    }
  }
}

.desktop_only_overlay {
  display: none;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: #333333;
  align-items: center;
  justify-content: center;
  color: #999;
  padding: 2em;
  z-index: 4;

  &__content {
    max-width: 350px;
    text-align: center;
    margin: auto;
    font-size: 1.2em;
    line-height: 1.4;
  }

  &__icon {
    opacity: 0.8;
    margin-bottom: 0.2em;
  }
}

$slideAnimDuration: 0.25s;
$slideAnimTranslateX: 50px;

.slideAnim {
  &-prev {
    &-enter-active {
      animation: slideAnimPrevEnter $slideAnimDuration;
    }

    &-leave-active {
      animation: slideAnimPrevLeave $slideAnimDuration;
    }
  }

  &-next {
    &-enter-active {
      animation: slideAnimNextEnter $slideAnimDuration;
    }

    &-leave-active {
      animation: slideAnimNextLeave $slideAnimDuration;
    }
  }
}

@keyframes slideAnimPrevEnter {
  0% {
    opacity: 0;
    transform: translateY(-$slideAnimTranslateX);
  }

  100% {
    opacity: 1;
    transform: translateY(0%);
  }
}

@keyframes slideAnimPrevLeave {
  0% {
    opacity: 1;
    transform: translateY(0%);
  }

  100% {
    opacity: 0;
    transform: translateY($slideAnimTranslateX);
  }
}

@keyframes slideAnimNextEnter {
  0% {
    opacity: 0;
    transform: translateY($slideAnimTranslateX);
  }

  100% {
    opacity: 1;
    transform: translateY(0%);
  }
}

@keyframes slideAnimNextLeave {
  0% {
    opacity: 1;
    transform: translateY(0%);
  }

  100% {
    opacity: 0;
    transform: translateY(-$slideAnimTranslateX);
  }
}

@media screen and (max-width: 1408px) {
  .steps_list {
    display: flex;
  }

  .steps_list__item {
    min-width: 130px;
    padding-right: 1em;
    flex-direction: column-reverse;
    align-items: normal;
    justify-content: flex-end;
  }
  .steps_list__item:not(:last-child) {
    padding-bottom: 0;
  }

  .steps_list__item:after {
    display: none;
  }

  .steps_list__item_title_text_big_screen {
    display: none;
  }

  .steps_list__item_title_text_small_screen {
    display: block;
    white-space: nowrap;
  }

  .steps_list__item_circle {
    $size: 24px;
    margin-bottom: 6px;
    width: $size;
    height: $size;
  }

  .steps_list__item_check {
    width: 11px;
  }

  @keyframes slideAnimPrevEnter {
    0% {
      opacity: 0;
      transform: translateX(-$slideAnimTranslateX);
    }

    100% {
      opacity: 1;
      transform: translateX(0%);
    }
  }

  @keyframes slideAnimPrevLeave {
    0% {
      opacity: 1;
      transform: translateX(0%);
    }

    100% {
      opacity: 0;
      transform: translateX($slideAnimTranslateX);
    }
  }

  @keyframes slideAnimNextEnter {
    0% {
      opacity: 0;
      transform: translateX($slideAnimTranslateX);
    }

    100% {
      opacity: 1;
      transform: translateX(0%);
    }
  }

  @keyframes slideAnimNextLeave {
    0% {
      opacity: 1;
      transform: translateX(0%);
    }

    100% {
      opacity: 0;
      transform: translateX(-$slideAnimTranslateX);
    }
  }
}

@media (max-width: 1060px) {
  .calculator_body,
  .steps_list {
    display: none;
  }

  .desktop_only_overlay {
    display: flex;
  }
}

@media (max-height: 793px) {
  .steps_list__item:not(:last-child) {
    padding-bottom: 50px;
  }
}

@media (max-height: 707px) {
  .steps_list__item:not(:last-child) {
    padding-bottom: 40px;
  }
}
</style>
